import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ExternalDataService, VoucherConfigurationDto, CreateVoucherConfigurationDto, PartnerDto, Zone, ScrapItemPriceDto } from '../../services/externalDataService.service';
import { AuthService } from '../../services/auth.service';
import { Observable, filter, take } from 'rxjs';

@Component({
  selector: 'app-voucher-configuration',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './voucher-configuration.component.html',
  styleUrl: './voucher-configuration.component.css'
})
export class VoucherConfigurationComponent implements OnInit {
  configurations: VoucherConfigurationDto[] = [];
  expandedRows: Set<string> = new Set();
  loading = false;
  errorMessage = '';
  
  // Form state for create/edit
  showCreateModal = false;
  showEditModal = false;
  selectedConfiguration: VoucherConfigurationDto | null = null;
  configurationForm: CreateVoucherConfigurationDto = {
    name: '',
    partnerId: '',
    partnerName: '',
    partnerLogo: null,
    validityPeriodInMonths: 0,
    zones: [],
    possibleVoucherAmounts: [],
    billAmountMultiplier: 0,
    defaultMultiplier: 0,
    itemMultipliers: []
  };

  // Partner list for dropdown
  partners: PartnerDto[] = [];
  selectedPartnerId: string | null = null;

  // Zones list for dropdown
  zones: Zone[] = [];

  // Scrap items list for dropdown
  scrapItems: ScrapItemPriceDto[] = [];

  // Filter
  filterPartnerId: string | null = null;

  constructor(
    private externalDataService: ExternalDataService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Wait for MSAL to be initialized before loading data
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => {
        this.loadPartners();
        this.loadZones();
        this.loadScrapItems();
        this.loadConfigurations();
      });
  }

  loadPartners(): void {
    this.externalDataService.getAllPartners().subscribe({
      next: (partners) => {
        this.partners = partners;
      },
      error: (error) => {
        console.error('Error loading partners:', error);
      }
    });
  }

  loadZones(): void {
    this.externalDataService.getAllZones().subscribe({
      next: (zones) => {
        // Filter out disabled and deleted zones
        this.zones = zones.filter(zone => !zone.disabled && !zone.deletedAt);
      },
      error: (error) => {
        console.error('Error loading zones:', error);
      }
    });
  }

  loadScrapItems(): void {
    this.externalDataService.getScrapPriceList().subscribe({
      next: (scrapItems) => {
        // Filter out inactive items
        this.scrapItems = scrapItems.filter(item => item.isActive);
      },
      error: (error) => {
        console.error('Error loading scrap items:', error);
      }
    });
  }

  loadConfigurations(): void {
    this.loading = true;
    this.errorMessage = '';
    
    this.externalDataService.getAllVoucherConfigurations(this.filterPartnerId || undefined).subscribe({
      next: (configurations) => {
        this.configurations = configurations;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading voucher configurations:', error);
        this.errorMessage = 'Failed to load voucher configurations. Please try again.';
        this.loading = false;
        this.configurations = [];
      }
    });
  }

  toggleRow(configId: string): void {
    if (this.expandedRows.has(configId)) {
      this.expandedRows.delete(configId);
    } else {
      this.expandedRows.add(configId);
    }
  }

  isRowExpanded(configId: string): boolean {
    return this.expandedRows.has(configId);
  }

  // Action methods
  openCreateModal(): void {
    this.configurationForm = {
      name: '',
      partnerId: '',
      partnerName: '',
      partnerLogo: null,
      validityPeriodInMonths: 0,
      zones: [],
      possibleVoucherAmounts: [],
      billAmountMultiplier: 0,
      defaultMultiplier: 0,
      itemMultipliers: []
    };
    this.selectedPartnerId = null;
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.configurationForm = {
      name: '',
      partnerId: '',
      partnerName: '',
      partnerLogo: null,
      validityPeriodInMonths: 0,
      zones: [],
      possibleVoucherAmounts: [],
      billAmountMultiplier: 0,
      defaultMultiplier: 0,
      itemMultipliers: []
    };
    this.selectedPartnerId = null;
  }

  // Removed onOverlayClick - modal should only close with close button

  openEditModal(config: VoucherConfigurationDto): void {
    this.selectedConfiguration = config;
    this.configurationForm = {
      name: config.name,
      partnerId: config.partnerId,
      partnerName: config.partnerName,
      partnerLogo: config.partnerLogo,
      validityPeriodInMonths: config.validityPeriodInMonths,
      zones: config.zones.map(zone => ({ ...zone })),
      possibleVoucherAmounts: [...config.possibleVoucherAmounts],
      billAmountMultiplier: config.billAmountMultiplier,
      defaultMultiplier: config.defaultMultiplier,
      itemMultipliers: config.itemMultipliers.map(item => ({ ...item }))
    };
    this.selectedPartnerId = config.partnerId;
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedConfiguration = null;
    this.configurationForm = {
      name: '',
      partnerId: '',
      partnerName: '',
      partnerLogo: null,
      validityPeriodInMonths: 0,
      zones: [],
      possibleVoucherAmounts: [],
      billAmountMultiplier: 0,
      defaultMultiplier: 0,
      itemMultipliers: []
    };
    this.selectedPartnerId = null;
  }

  // Removed onEditOverlayClick - modal should only close with close button

  onPartnerSelect(): void {
    const selectedPartner = this.partners.find(p => p.id === this.selectedPartnerId);
    if (selectedPartner) {
      this.configurationForm.partnerId = selectedPartner.id;
      this.configurationForm.partnerName = selectedPartner.name;
    }
  }

  createConfiguration(): void {
    if (!this.validateForm()) {
      return;
    }

    this.loading = true;
    this.externalDataService.createVoucherConfiguration(this.configurationForm).subscribe({
      next: (config) => {
        this.loadConfigurations();
        this.closeCreateModal();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error creating voucher configuration:', error);
        this.errorMessage = 'Failed to create voucher configuration. Please try again.';
        this.loading = false;
      }
    });
  }

  updateConfiguration(): void {
    if (!this.selectedConfiguration || !this.validateForm()) {
      return;
    }

    this.loading = true;
    const updateData: VoucherConfigurationDto = {
      ...this.selectedConfiguration,
      ...this.configurationForm
    };

    this.externalDataService.updateVoucherConfiguration(updateData).subscribe({
      next: (config) => {
        this.loadConfigurations();
        this.closeEditModal();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error updating voucher configuration:', error);
        this.errorMessage = 'Failed to update voucher configuration. Please try again.';
        this.loading = false;
      }
    });
  }

  deleteConfiguration(config: VoucherConfigurationDto): void {
    if (confirm(`Are you sure you want to delete voucher configuration "${config.name}"? This action cannot be undone.`)) {
      this.loading = true;
      this.externalDataService.deleteVoucherConfiguration(config.id, config.partnerId).subscribe({
        next: () => {
          this.loadConfigurations();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error deleting voucher configuration:', error);
          this.errorMessage = 'Failed to delete voucher configuration. Please try again.';
          this.loading = false;
        }
      });
    }
  }

  validateForm(): boolean {
    if (!this.configurationForm.name?.trim()) {
      this.errorMessage = 'Configuration name is required.';
      return false;
    }
    if (!this.configurationForm.partnerId?.trim()) {
      this.errorMessage = 'Partner is required.';
      return false;
    }
    if (this.configurationForm.validityPeriodInMonths <= 0) {
      this.errorMessage = 'Validity period must be greater than 0.';
      return false;
    }
    if (this.configurationForm.billAmountMultiplier <= 0) {
      this.errorMessage = 'Bill amount multiplier must be greater than 0.';
      return false;
    }
    if (this.configurationForm.defaultMultiplier < 0) {
      this.errorMessage = 'Default multiplier must be greater than or equal to 0.';
      return false;
    }
    this.errorMessage = '';
    return true;
  }

  addZone(): void {
    this.configurationForm.zones.push({
      zoneId: '',
      zoneName: ''
    });
  }

  onZoneSelect(index: number): void {
    const selectedZoneId = this.configurationForm.zones[index].zoneId;
    const selectedZone = this.zones.find(z => z.id === selectedZoneId);
    if (selectedZone) {
      this.configurationForm.zones[index].zoneId = selectedZone.id;
      this.configurationForm.zones[index].zoneName = selectedZone.name;
    }
  }

  onScrapItemSelect(index: number): void {
    const selectedScrapItemId = this.configurationForm.itemMultipliers[index].scrapItemId;
    const selectedScrapItem = this.scrapItems.find(item => item.scrapItemId === selectedScrapItemId);
    if (selectedScrapItem) {
      this.configurationForm.itemMultipliers[index].scrapItemId = selectedScrapItem.scrapItemId;
      this.configurationForm.itemMultipliers[index].scrapItemName = selectedScrapItem.scrapName;
    }
  }

  removeZone(index: number): void {
    this.configurationForm.zones.splice(index, 1);
  }

  addVoucherAmount(): void {
    this.configurationForm.possibleVoucherAmounts.push(0);
  }

  removeVoucherAmount(index: number): void {
    this.configurationForm.possibleVoucherAmounts.splice(index, 1);
  }

  addItemMultiplier(): void {
    this.configurationForm.itemMultipliers.push({
      scrapItemId: '',
      scrapItemName: '',
      multiplier: 0
    });
  }

  removeItemMultiplier(index: number): void {
    this.configurationForm.itemMultipliers.splice(index, 1);
  }

  applyFilter(): void {
    this.loadConfigurations();
  }

  clearFilter(): void {
    this.filterPartnerId = null;
    this.loadConfigurations();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  }
}

